import * as React from "react";
import StoreLocator from "./StoreLocator";
import StoreMap from "./StoreMap";
import type { Store } from "@app/interface/Stores";
import { useStoreLocator } from "@app/hooks/useStoreLocator";
import { getQueryParam } from "@app/utils/queryParams";
import config from "@app/config";

type Props = {
  stores: Store[];
};

const StoresPage: React.FC<Props> = ({ stores }) => {
  const { getCoords, getDistance } = useStoreLocator();
  const sortedStores = stores.sort((a, b) => (a.name && b.name ? (a.name < b.name ? -1 : 1) : 0));

  const [activeStores, setActiveStores] = React.useState<Store[]>(sortedStores);
  const [focusedStore, setFocusedStore] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const [locationSearched, setLocationSearched] = React.useState<boolean>(false);
  const [mapLocation, setMapLocation] = React.useState<
    { latitude: number; longitude: number; zoom: number } | undefined
  >();
  const [searchCoordinates, setSearchCoordinates] = React.useState<
    { latitude: number; longitude: number } | undefined
  >();

  const selectedStoreId = getQueryParam("store");

  const countries = [...new Set([...(stores.map((store: Store) => store.address.country) || [])])].filter(Boolean);
  const countryStores = {} as { [key: string]: { [key: string]: Store[] } };
  countries.forEach((country) => {
    const countryList = activeStores.filter((store) => store.address?.country === country);

    const stateStores = {} as { [key: string]: Store[] };
    const states = [...new Set([...(countryList.map((store: Store) => store.address.state) || [])])].filter(Boolean);
    states.forEach((state) => {
      if (state !== undefined) {
        stateStores[state] = countryList
          .filter((store) => store.address?.state === state)
          .sort((a, b) => (a.name && b.name ? (a.name < b.name ? -1 : 1) : 0));
      }
    });
    countryStores[country] = stateStores;
  });

  const goToCoords = React.useCallback((lat: number, lng: number) => {
    React.startTransition(() => {
      setMapLocation({ latitude: lat, longitude: lng, zoom: 11.25 });
    });
  }, []);

  const focusOnStore = React.useCallback(
    (handle: string | null) => {
      setFocusedStore(handle);
      const store = activeStores.find((store) => store.handle === handle);
      if (store?.address) {
        goToCoords(store.address.latitude, store.address.longitude);
      }
    },
    [goToCoords, activeStores],
  );

  const getUserLocation = async () => {
    const coords = await getCoords();
    if (coords) {
      const storeList = [...activeStores];
      storeList.forEach((store) => {
        store.distance = getDistance(
          { lat: coords.lat, lng: coords.lng },
          { lat: store.address.latitude, lng: store.address.longitude },
        );
      });
      const sortedStores = storeList.sort((a, b) =>
        a.distance && b.distance ? (a.distance < b.distance ? -1 : 1) : 0,
      );
      setActiveStores(sortedStores);
      setLocationSearched(true);
      setFocusedStore(null);
      if (sortedStores[0].address) {
        goToCoords(sortedStores[0].address.latitude, sortedStores[0].address.longitude);
      } else {
        goToCoords(coords.lat, coords.lng);
      }
      setError(null);
    }
  };

  const markers = activeStores.map((store) => {
    return { latitude: store.address.latitude, longitude: store.address.longitude };
  });

  const handleSearch = async () => {
    if (!search) {
      setError("Please enter a location or postcode.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(search)}.json?access_token=${config.mapbox.accessToken}&country=AU&limit=1&types=postcode,locality,place`,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch location data.");
      }

      const data = await response.json();
      const coordinates = data?.features?.[0]?.center;

      if (coordinates) {
        if (
          !searchCoordinates ||
          searchCoordinates.latitude !== coordinates[1] ||
          searchCoordinates.longitude !== coordinates[0]
        ) {
          setSearchCoordinates({ latitude: coordinates[1], longitude: coordinates[0] });

          const updatedStores = [...stores];
          updatedStores.forEach((store) => {
            store.distance = getDistance(
              { lat: coordinates[1], lng: coordinates[0] },
              { lat: store.address.latitude, lng: store.address.longitude },
            );
          });

          const sortedStores = updatedStores
            .filter((store) => store.find_in_store === true)
            .sort((a, b) => (a.distance && b.distance ? (a.distance < b.distance ? -1 : 1) : 0));

          setActiveStores(sortedStores);
          setLocationSearched(true);
          setFocusedStore(null);

          if (sortedStores[0]?.address) {
            goToCoords(sortedStores[0].address.latitude, sortedStores[0].address.longitude);
          } else {
            goToCoords(coordinates[1], coordinates[0]);
          }
        }
      } else {
        setError("No results found, please enter another address.");
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred while searching. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const selectedStore = stores.find((store) => store.handle === selectedStoreId);

  return (
    <div className="stores-page__wrapper">
      <StoreLocator
        query={search}
        setQuery={setSearch}
        error={error}
        loading={loading}
        handleSearch={handleSearch}
        handleGeoLocate={getUserLocation}
        countryStores={countryStores}
        focusOnStore={focusOnStore}
        selectedStore={selectedStore}
        locationSearched={locationSearched}
        activeStores={activeStores}
      />
      <StoreMap center={mapLocation} markers={markers} />
    </div>
  );
};

export default StoresPage;
